import type { Lang } from '../../lib/i18n'

// R503 (d): the Settings screen's own dictionary, the same shape as
// features/dispatch/dispatch-i18n.ts. The Arabic is Rook's, from the vault (dcf2ae53f, Rook
// #67746), copied byte for byte by DC's pinned fill script; nobody types it here.
const EN = {
  title: 'Settings',
  loading: 'Loading settings\u2026',
  ownerOnly: 'This is for the owner.',
  loadFailed: "Couldn't load the settings.",
  sectionRing: 'Ring',
  sectionWriteoff: 'Write-off reasons',
  save: 'Save',
  saving: 'Saving\u2026',
  saved: 'Saved',
  noChanges: 'No changes to save',
  error403: 'Only the owner can change this.',
  error404: 'This setting is not known to the server.',
  errorNetwork: "Couldn't reach the server. Check the connection and try again.",
  errorOther: "Couldn't save. Try again.",
  lastChanged: 'Last changed {time}',
  neverChanged: 'Never changed (the default)',
  rangeHint: 'From {min} to {max}',
  enterNumber: 'Enter a number',
  key_ring_interval_s: 'Ring repeats every (seconds)',
  key_ring_loudness: 'Loudness',
  key_ring_pending_threshold_s: 'Ring for a new order not confirmed after (seconds)',
  key_ring_kitchen_threshold_s: 'Ring when the kitchen has not started after (seconds)',
  key_ring_final_check_threshold_s: 'Ring when the final check has not started after (seconds)',
  key_ring_held_card_threshold_s: 'Ring for a held Cockpit card after (seconds)',
  key_ring_heard_pause_s: '"Heard it" pauses one device for (seconds)',
  key_ring_quiet_hours: 'Quiet hours (Beirut time)',
  key_ring_screens: 'Which screens ring, and for what',
  key_stock_writeoff_reasons: 'Reasons a write-off can give',
  quietOff: 'Off',
  quietOn: 'On',
  quietStart: 'From',
  quietEnd: 'To',
  quietNeedsTimes: 'Set both times',
  screen_cockpit: 'Cockpit',
  screen_board: 'Board',
  screen_kitchen_phone: 'Kitchen phone',
  screen_kitchen_tv: 'Kitchen TV',
  screen_till: 'Till',
  screenOn: 'Rings on this screen',
  reasonsLegend: 'Rings for',
  reason_pending: 'New order not confirmed',
  reason_held_card: 'Held Cockpit card',
  reason_kitchen_not_started: 'Kitchen not started',
  reason_final_check_not_started: 'Final check not started',
  woCode: 'Code',
  woLabel: 'Name',
  woNoteRequired: 'Note required',
  woAdd: 'Add a reason',
  woRemove: 'Remove',
  woCodeFixed: 'A code is fixed once saved.',
  woAtMax: 'The list is full ({max} reasons).',
  woAtMin: 'The list keeps at least {min} reason.',
}

type SettingsKey = keyof typeof EN

const AR: Record<SettingsKey, string> = {
  title: 'الإعدادات',
  loading: 'جارٍ تحميل الإعدادات…',
  ownerOnly: 'هذه الصفحة للمالك فقط.',
  loadFailed: 'تعذّر تحميل الإعدادات.',
  sectionRing: 'الرنّة',
  sectionWriteoff: 'أسباب الشطب',
  save: 'حفظ',
  saving: 'جارٍ الحفظ…',
  saved: 'تم الحفظ',
  noChanges: 'لا تغييرات للحفظ',
  error403: 'المالك وحده يمكنه تغيير هذا.',
  error404: 'هذا الإعداد غير معروف لدى الخادم.',
  errorNetwork: 'تعذّر الوصول إلى الخادم. تحقّق من الاتصال وحاول مجددًا.',
  errorOther: 'تعذّر الحفظ. حاول مجددًا.',
  lastChanged: 'آخر تغيير {time}',
  neverChanged: 'لم يتغيّر قط (القيمة الافتراضية)',
  rangeHint: 'من {min} إلى {max}',
  enterNumber: 'أدخل رقمًا',
  key_ring_interval_s: 'تتكرّر الرنّة كل (ثوانٍ)',
  key_ring_loudness: 'علوّ الصوت',
  key_ring_pending_threshold_s: 'رنّة لطلب جديد لم يُؤكَّد بعد (ثوانٍ)',
  key_ring_kitchen_threshold_s: 'رنّة عندما لا يبدأ المطبخ بعد (ثوانٍ)',
  key_ring_final_check_threshold_s: 'رنّة عندما لا يبدأ الفحص النهائي بعد (ثوانٍ)',
  key_ring_held_card_threshold_s: 'رنّة لبطاقة معلّقة في لوحة القيادة بعد (ثوانٍ)',
  key_ring_heard_pause_s: '«سمعته» يوقف الرنّة على جهاز واحد لمدة (ثوانٍ)',
  key_ring_quiet_hours: 'ساعات الهدوء (بتوقيت بيروت)',
  key_ring_screens: 'أي الشاشات ترنّ، ولأي سبب',
  key_stock_writeoff_reasons: 'الأسباب المتاحة عند الشطب',
  quietOff: 'متوقّف',
  quietOn: 'مفعّل',
  quietStart: 'من',
  quietEnd: 'إلى',
  quietNeedsTimes: 'حدّد الوقتين',
  screen_cockpit: 'لوحة القيادة',
  screen_board: 'اللوحة',
  screen_kitchen_phone: 'هاتف المطبخ',
  screen_kitchen_tv: 'شاشة المطبخ',
  screen_till: 'الصندوق',
  screenOn: 'ترنّ على هذه الشاشة',
  reasonsLegend: 'ترنّ من أجل',
  reason_pending: 'طلب جديد غير مؤكَّد',
  reason_held_card: 'بطاقة معلّقة في لوحة القيادة',
  reason_kitchen_not_started: 'المطبخ لم يبدأ',
  reason_final_check_not_started: 'الفحص النهائي لم يبدأ',
  woCode: 'الرمز',
  woLabel: 'الاسم',
  woNoteRequired: 'ملاحظة إلزامية',
  woAdd: 'إضافة سبب',
  woRemove: 'إزالة',
  woCodeFixed: 'الرمز لا يتغيّر بعد الحفظ.',
  woAtMax: 'القائمة ممتلئة ({max} أسباب).',
  woAtMin: 'تحتفظ القائمة بسبب واحد على الأقل ({min}).',
}

const SETTINGS_DICT: Record<Lang, Record<SettingsKey, string>> = { en: EN, ar: AR }

export type { SettingsKey }

export function sx(lang: Lang, key: SettingsKey, vars: Record<string, string | number> = {}): string {
  let out = SETTINGS_DICT[lang][key]
  for (const [name, value] of Object.entries(vars)) out = out.replace(`{${name}}`, String(value))
  return out
}

/** A label looked up by a key built from server data (a setting key, a screen, a reason); the raw name when this dictionary has none. */
export function sxOr(lang: Lang, key: string, fallback: string): string {
  return Object.prototype.hasOwnProperty.call(EN, key) ? sx(lang, key as SettingsKey) : fallback
}

export const SETTINGS_EN = EN
